<template>
  <div class="q-mt-md">
    <h3 class="text-bold q-m-md inline-block">
      {{ headerMessage }}
    </h3>

    <q-form class="row justify-evenly">
      <!-- Input to set token, note that this token is persisted state -->
      <div class="q-mt-md token-input col-8">
        <q-input
          v-model="form.token"
          label="Token"
          :type="hideToken ? 'password' : 'text'"
          hint="Token for clear cache"
        >
          <template v-slot:append>
            <font-awesome-icon
              :icon="hideToken ? 'eye-slash' : 'eye'"
              class="cursor-pointer"
              size="sm"
              @click="hideToken = !hideToken"
            />
          </template>
        </q-input>
      </div>

      <q-card class="q-pa-lg q-mt-xl card-container col-6">
        <q-tabs
          v-model="form.tab"
          dense
          class="text-grey"
          active-color="primary"
          indicator-color="primary"
          align="justify"
          narrow-indicator
        >
          <q-tab name="all" label="Clear All" />
          <q-tab name="selected" label="Clear Selected" />
        </q-tabs>

        <q-separator />

        <q-tab-panels v-model="form.tab" animated>
          <!-- From tab to clear all cache -->
          <q-tab-panel name="all">
            <div class="text-h6">Clear All Caches</div>

            <div class="form-content">
              <div class="env-container q-mt-md">
                <div class="env-header">Environment</div>
                <q-radio
                  v-model="form.env"
                  val="stage"
                  label="Stage"
                  color="secondary"
                  class="env-radio"
                />
                <q-radio
                  v-model="form.env"
                  class="q-ml-md env-radio"
                  val="prod"
                  label="Production"
                  color="primary"
                />
              </div>

              <div class="env-container q-mt-lg">
                <div class="env-header">Cache Method</div>
                <q-radio
                  v-model="form.cacheMethod"
                  val="invalidate"
                  label="Invalidate"
                  color="secondary"
                  class="env-radio"
                />
                <q-radio
                  v-model="form.cacheMethod"
                  class="q-ml-md env-radio"
                  val="delete"
                  label="Delete"
                  color="primary"
                />
              </div>

              <div class="btn-container q-mt-lg">
                <ButtonGeneral
                  :buttonClick="() => handlerConfirm()"
                  buttonText="Clear Cache"
                  :buttonDisable="isLoading || !form.token"
                />
                <q-tooltip v-if="!form.token" self="center left">
                  Check the Token
                </q-tooltip>
              </div>
            </div>
          </q-tab-panel>

          <!-- From tab to clear selected cache -->
          <q-tab-panel name="selected">
            <div class="text-h6">Clear Selected Cache</div>
            <div class="form-content">
              <div class="env-container q-mt-md">
                <div class="env-header">Environment</div>
                <q-radio
                  v-model="form.env"
                  val="stage"
                  label="Stage"
                  color="secondary"
                  class="env-radio"
                />
                <q-radio
                  v-model="form.env"
                  class="q-ml-md env-radio"
                  val="prod"
                  label="Production"
                  color="primary"
                />
              </div>

              <div class="env-container q-mt-lg">
                <div class="env-header">Cache Method</div>
                <q-radio
                  v-model="form.cacheMethod"
                  val="invalidate"
                  label="Invalidate"
                  color="secondary"
                  class="env-radio"
                />
                <q-radio
                  v-model="form.cacheMethod"
                  class="q-ml-md env-radio"
                  val="delete"
                  label="Delete"
                  color="primary"
                />
              </div>

              <div class="q-mt-lg">
                <q-select
                  v-model="form.clearSelectedCache"
                  multiple
                  clearable
                  label="Clear sections"
                  :options="form.clearOptions"
                />
              </div>

              <div class="btn-container q-mt-lg">
                <ButtonGeneral
                  :buttonClick="() => handlerConfirm()"
                  buttonText="Clear Cache"
                  :buttonDisable="isLoading || !form.clearSelectedCache"
                />
              </div>
            </div>
          </q-tab-panel>
        </q-tab-panels>
      </q-card>

      <!-- Show API response for clear cache content -->
      <q-card class="col-5 q-pa-lg q-mt-xl card-container">
        <h6>Response JSON:</h6>
        <div v-if="isLoading">
          <q-spinner-ios color="primary" size="4em" />
        </div>

        <div v-else class="code-text">
          <strong>
            Status Code:
            <span :style="codeStatus()">
              {{ responseJson.statusCode }} <br />
            </span>
          </strong>

          <strong> Message: </strong> <br />
          {{ responseJson.message }}
        </div>
      </q-card>
    </q-form>
    <ProdCacheModalConfirm
      :key="getIsOpenModal"
      :modalText="confirmModalText"
      :handlerConfirm="confirmBanner"
      :confirmCode="rndProdConfirmation"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import ButtonGeneral from "../components/button/ButtonGeneral.vue";
import ProdCacheModalConfirm from "@/components/modals/ProdCacheModalConfirm.vue";

export default {
  name: "ClearCacheView",

  data() {
    return {
      form: {
        env: "",
        tab: "all",
        clearOptions: ["Yellow Banner", "Promos", "Games", "Home Page"],
        clearSelectedCache: null,
        cacheMethod: "invalidate",
        token: "",
      },
      headerMessage: "Clear Cache",
      isLoading: false,
      responseJson: {},
      hideToken: false,
      confirmModalText:
        "You are trying to clear all cache in PROD, please insert eh code below:",
      rndProdConfirmation: null,
    };
  },

  components: { ButtonGeneral, ProdCacheModalConfirm },

  computed: {
    ...mapGetters({ getEnv: "selectEnv/getEnv" }),
    ...mapGetters({ getToken: "generalPersist/getToken" }),
    ...mapGetters({ getIsOpenModal: "confirmModal/getIsOpenModal" }),
  },

  created() {
    if (this.userRoles && !this.userRoles.includes("Admin")) {
      this.$router.push("/");
    }
  },

  mounted() {
    this.form.token = this.getToken;
    if (this.getEnv === "prod") {
      this.form.env = "prod";
    } else {
      this.form.env = "stage";
    }
  },

  methods: {
    ...mapActions({
      setClearCache: "general/setClearCache",
    }),
    ...mapActions({
      setOpenModal: "confirmModal/setOpenModal",
    }),

    async handlerConfirm() {
      this.isLoading = true;
      this.responseJson = "";
      // Check if will clear all cache in prod to request a safety token
      if (this.form.env === "prod" && this.form.tab === "all") {
        this.isLoading = false;
        this.rndProdConfirmation = Math.floor(1000 + Math.random() * 9000);
        this.setOpenModal(true);
        return;
      }
      await this.setClearCache({ form: this.form }).then((res) => {
        this.isLoading = false;
        this.responseJson = res;
      });
    },

    // Check conditions to confirm modal and clear de cache
    async confirmBanner() {
      this.isLoading = true;
      this.setOpenModal(false);
      await this.setClearCache({ form: this.form }).then((res) => {
        this.isLoading = false;
        this.responseJson = res;
      });
      this.isLoading = false;
    },

    codeStatus() {
      if (this.responseJson.statusCode) {
        if (this.responseJson.statusCode < 400) {
          return "color: green;";
        } else {
          return "color: red;";
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.card-container {
  padding: 60px;
}

.env-container {
  text-align: center;
}

.env-header {
  font-size: 20px;
  height: 30px;
}

.env-radio {
  font-size: 20px;
}

.btn-container {
  float: right;
}

.code-text {
  font-size: 24px;
}
</style>
